import { certificationApi } from '@/lib/certification-api';
import {
  CreateCourseData,
  GetCourseData,
  CreateModuleData,
  GetModuleData,
  CreateLessonData,
  GetLessonData,
  CreateQuizData,
  GetQuizData,
  GetCertificateData,
  ICertificateRepository,
  ICourseRepository,
  ILessonRepository,
  IModuleRepository,
  IQuizRepository,
  IUserRepository,
} from '../repository/interfaces';
import { ICertificationUseCase } from './interfaces';

/**
 * Certification/Course Use Case
 * Handles complex operations involving courses, modules, lessons, quizzes, and certificates
 * User Stories: 10, 11, 12
 */
export class CertificationUseCase implements ICertificationUseCase {
  constructor(
    private courseRepo: ICourseRepository,
    private moduleRepo: IModuleRepository,
    private lessonRepo: ILessonRepository,
    // Quiz grading moved to the server; kept so the constructor signature is unchanged.
    protected quizRepo: IQuizRepository,
    private certificateRepo: ICertificateRepository,
    private userRepo: IUserRepository
  ) {
    // Removed BaseUseCase
  }

  /**
   * Create a complete certification with modules, lessons, and quizzes
   * User Story 10: As a creator, I build certifications as modules → lessons, attach media, and set prerequisites
   */
  async createCertification(
    courseData: CreateCourseData,
    modules: Array<{
      moduleData: CreateModuleData;
      lessons: CreateLessonData[];
      quizzes: CreateQuizData[];
    }>
  ): Promise<{
    course: GetCourseData;
    modules: GetModuleData[];
    lessons: GetLessonData[];
    quizzes: GetQuizData[];
  }> {
    console.log('[CertificationUseCase.createCertification] START - CourseData:', courseData.title, 'Modules:', modules.length);
    try {
      console.log('[CertificationUseCase.createCertification] Creating course');
      // Create the main course/certification
      const course = await this.courseRepo.createCourse(courseData);
      console.log('[CertificationUseCase.createCertification] Course created, ID:', course.id);

      const createdModules: GetModuleData[] = [];
      const createdLessons: GetLessonData[] = [];
      const createdQuizzes: GetQuizData[] = [];

      console.log('[CertificationUseCase.createCertification] Creating', modules.length, 'modules with content');
      // Create modules with their lessons and quizzes
      for (const moduleInput of modules) {
        console.log('[CertificationUseCase.createCertification] Creating module:', moduleInput.moduleData.title);
        // Create module
        const module = await this.moduleRepo.createModule({
          ...moduleInput.moduleData,
          courseId: course.id
        });
        createdModules.push(module);
        console.log('[CertificationUseCase.createCertification] Module created, ID:', module.id);

        // Create lessons for this module
        console.log('[CertificationUseCase.createCertification] Creating', moduleInput.lessons.length, 'lessons for module');
        for (const lessonData of moduleInput.lessons) {
          const lesson = await this.moduleRepo.createModuleLesson(module.id, lessonData);
          createdLessons.push(lesson);
        }

        // Create quizzes for this module
        console.log('[CertificationUseCase.createCertification] Creating', moduleInput.quizzes.length, 'quizzes for module');
        for (const quizData of moduleInput.quizzes) {
          const quiz = await this.moduleRepo.createModuleQuiz(module.id, quizData);
          createdQuizzes.push(quiz);
        }
      }

      const result = {
        course,
        modules: createdModules,
        lessons: createdLessons,
        quizzes: createdQuizzes
      };
      console.log('[CertificationUseCase.createCertification] SUCCESS - Created:', createdModules.length, 'modules,', createdLessons.length, 'lessons,', createdQuizzes.length, 'quizzes');
      return result;
    } catch (error) {
      console.error('[CertificationUseCase.createCertification] ERROR:', error);
      throw error;
    }
  }

  /**
   * Enroll a student in a certification
   * User Story 11: As a student, I enroll, track progress, and complete quizzes/exams per module
   */
  async enrollStudent(
    userId: string,
    courseId: string
  ): Promise<{
    enrollment: any;
    course: GetCourseData;
    modules: GetModuleData[];
  }> {
    console.log('[CertificationUseCase.enrollStudent] START - UserId:', userId, 'CourseId:', courseId);
    try {
      console.log('[CertificationUseCase.enrollStudent] Checking existing enrollments');
      // Check if user is already enrolled
      const existingEnrollments = await this.courseRepo.getUserEnrollments(userId);
      const alreadyEnrolled = existingEnrollments.some(e => e.courseId === courseId);
      console.log('[CertificationUseCase.enrollStudent] Existing enrollments:', existingEnrollments.length, 'Already enrolled:', alreadyEnrolled);

      if (alreadyEnrolled) {
        console.log('[CertificationUseCase.enrollStudent] ERROR: User already enrolled');
        throw new Error('User is already enrolled in this course');
      }

      console.log('[CertificationUseCase.enrollStudent] Creating enrollment');
      // Create enrollment
      const enrollment = await this.courseRepo.enrollUser({
        userId: userId,
        courseId: courseId,
        enrollmentStatus: 'enrolled'
      });
      console.log('[CertificationUseCase.enrollStudent] Enrollment created');

      console.log('[CertificationUseCase.enrollStudent] Getting course details');
      // Get course details
      const course = await this.courseRepo.getCourseById(courseId);
      if (!course) {
        console.log('[CertificationUseCase.enrollStudent] ERROR: Course not found');
        throw new Error('Course not found');
      }

      console.log('[CertificationUseCase.enrollStudent] Getting course modules');
      // Get course modules
      const modules = await this.moduleRepo.getModulesByCourse(courseId);

      const result = {
        enrollment,
        course,
        modules
      };
      console.log('[CertificationUseCase.enrollStudent] SUCCESS - Course:', course.title, 'Modules:', modules.length);
      return result;
    } catch (error) { 
      console.error('[CertificationUseCase.enrollStudent] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get student's certification progress
   * User Story 11: As a student, I enroll, track progress, and complete quizzes/exams per module
   */
  async getStudentProgress(
    userId: string,
    courseId: string
  ): Promise<{
    enrollment: any;
    course: GetCourseData;
    modules: Array<{
      module: GetModuleData;
      lessons: GetLessonData[];
      quizzes: GetQuizData[];
      completedLessons: number;
      totalLessons: number;
      quizScores: Array<{ quizId: string; score: number; passed: boolean }>;
    }>;
    overallProgress: {
      completedModules: number;
      totalModules: number;
      progressPercentage: number;
    };
  }> {
    console.log('[CertificationUseCase.getStudentProgress] START - UserId:', userId, 'CourseId:', courseId);
    try {
      console.log('[CertificationUseCase.getStudentProgress] Getting enrollment information');
      // Get enrollment
      const enrollments = await this.courseRepo.getUserEnrollments(userId);
      const enrollment = enrollments.find(e => e.courseId === courseId);
      console.log('[CertificationUseCase.getStudentProgress] Found enrollments:', enrollments.length);

      if (!enrollment) {
        console.log('[CertificationUseCase.getStudentProgress] ERROR: Student not enrolled');
        throw new Error('Student is not enrolled in this course');
      }

      console.log('[CertificationUseCase.getStudentProgress] Getting course and modules');
      // Get course and modules
      const course = await this.courseRepo.getCourseById(courseId);
      if (!course) {
        console.log('[CertificationUseCase.getStudentProgress] ERROR: Course not found');
        throw new Error('Course not found');
      }

      const modules = await this.moduleRepo.getModulesByCourse(courseId);
      console.log('[CertificationUseCase.getStudentProgress] Processing', modules.length, 'modules');
      const moduleProgress = [];

      let totalCompletedModules = 0;

      for (const module of modules) {
        console.log('[CertificationUseCase.getStudentProgress] Processing module:', module.title);
        // Get lessons and quizzes for this module
        const lessons = await this.moduleRepo.getModuleLessons(module.id);
        const quizzes = await this.moduleRepo.getModuleQuizzes(module.id);
        console.log('[CertificationUseCase.getStudentProgress] Module has', lessons.length, 'lessons and', quizzes.length, 'quizzes');

        // For now, assume all lessons are completed if enrollment exists
        // In a real implementation, you'd track individual lesson completion
        const completedLessons = lessons.length; // Simplified
        const totalLessons = lessons.length;

        // Get quiz attempts for this user
        const quizScores = [];
        for (const quiz of quizzes) {
          // This would need to be implemented in quiz repository
          // const attempts = await this.quizRepo.getQuizAttemptsByUser(userId, quiz.id);
          // For now, return empty array
        }

        moduleProgress.push({
          module,
          lessons,
          quizzes,
          completedLessons,
          totalLessons,
          quizScores
        });

        if (completedLessons === totalLessons) {
          totalCompletedModules++;
        }
      }

      const overallProgress = {
        completedModules: totalCompletedModules,
        totalModules: modules.length,
        progressPercentage: modules.length > 0 ? (totalCompletedModules / modules.length) * 100 : 0
      };
      console.log('[CertificationUseCase.getStudentProgress] Calculated progress:', Math.round(overallProgress.progressPercentage), '%');

      const result = {
        enrollment,
        course,
        modules: moduleProgress,
        overallProgress
      };
      console.log('[CertificationUseCase.getStudentProgress] SUCCESS - Progress:', Math.round(overallProgress.progressPercentage), '%, Modules processed:', moduleProgress.length);
      return result;
    } catch (error) { 
      console.error('[CertificationUseCase.getStudentProgress] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Complete a lesson for a student
   * User Story 11: As a student, I enroll, track progress, and complete quizzes/exams per module
   */
  async completeLesson(
    userId: string,
    lessonId: string
  ): Promise<{
    lesson: GetLessonData;
    nextLesson?: GetLessonData;
  }> {
    console.log('[CertificationUseCase.completeLesson] START - UserId:', userId, 'LessonId:', lessonId);
    try {
      console.log('[CertificationUseCase.completeLesson] Getting lesson details');
      // Verify student is enrolled in the course containing this lesson
      const lesson = await this.lessonRepo.getLessonById(lessonId);
      if (!lesson) {
        console.log('[CertificationUseCase.completeLesson] ERROR: Lesson not found');
        throw new Error('Lesson not found');
      }
      console.log('[CertificationUseCase.completeLesson] Found lesson:', lesson.title);

      console.log('[CertificationUseCase.completeLesson] Verifying enrollment');
      // Get module to find course
      const modules = await this.moduleRepo.getModules({ courseId: lesson.courseId });
      if (modules.length === 0) {
        console.log('[CertificationUseCase.completeLesson] ERROR: Module not found for lesson');
        throw new Error('Module not found for lesson');
      }

      const module = modules[0];
      const enrollments = await this.courseRepo.getUserEnrollments(userId);
      const isEnrolled = enrollments.some(e => e.courseId === lesson.courseId);
      console.log('[CertificationUseCase.completeLesson] Enrollment status:', isEnrolled);

      if (!isEnrolled) {
        console.log('[CertificationUseCase.completeLesson] ERROR: Student not enrolled');
        throw new Error('Student is not enrolled in this course');
      }

      // Mark lesson as completed (in a real implementation, you'd store completion records)
      console.log('[CertificationUseCase.completeLesson] Marking lesson as completed');
      console.log(`[CertificationUseCase.completeLesson] Lesson ${lessonId} completed by user ${userId}`);

      console.log('[CertificationUseCase.completeLesson] Finding next lesson');
      // Get next lesson in the module
      const allLessons = await this.moduleRepo.getModuleLessons(module.id);
      const currentIndex = allLessons.findIndex(l => l.id === lessonId);
      const nextLesson = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : undefined;
      console.log('[CertificationUseCase.completeLesson] Next lesson:', nextLesson ? nextLesson.title : 'None');

      const result = {
        lesson,
        nextLesson
      };
      console.log('[CertificationUseCase.completeLesson] SUCCESS - Lesson completed:', lesson.title, 'Has next:', !!nextLesson);
      return result;
    } catch (error) { 
      console.error('[CertificationUseCase.completeLesson] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Submit quiz attempt and check for certification completion
   * User Story 11: As a student, I enroll, track progress, and complete quizzes/exams per module
   * User Story 12: As the system, enforce module order and pass thresholds; store attempts
   */
  async submitQuizAttempt(
    userId: string,
    quizId: string,
    answers: Record<string, any>
  ): Promise<{
    attempt: any;
    passed: boolean;
    score: number;
    certificate?: GetCertificateData;
  }> {
    // Graded on the server against the answer key; the server also decides
    // course completion and mints the certificate. (This used to invent a
    // random score and create the certificate from the browser.)
    const result = await certificationApi.submitQuizAttempt(quizId, answers, 0);
    let certificate: GetCertificateData | undefined;
    if (result.certificateId) {
      certificate = (await this.certificateRepo.getCertificateById(result.certificateId)) || undefined;
    }
    return {
      attempt: { id: result.attemptId, userId, quizId, score: result.score, passed: result.passed },
      passed: result.passed,
      score: result.score,
      certificate,
    };
  }

  /**
   * Get certification dashboard for a student
   */
  async getStudentCertifications(
    userId: string
  ): Promise<{
    enrolledCourses: Array<{
      course: GetCourseData;
      progress: number;
      status: string;
      certificate?: GetCertificateData;
    }>;
    completedCertifications: GetCertificateData[];
  }> {
    console.log('[CertificationUseCase.getStudentCertifications] START - UserId:', userId);
    try {
      console.log('[CertificationUseCase.getStudentCertifications] Getting user enrollments');
      // Get user enrollments
      const enrollments = await this.courseRepo.getUserEnrollments(userId);
      console.log('[CertificationUseCase.getStudentCertifications] Found', enrollments.length, 'enrollments');

      const enrolledCourses = [];
      for (const enrollment of enrollments) {
        console.log('[CertificationUseCase.getStudentCertifications] Processing enrollment for course:', enrollment.courseId);
        const course = await this.courseRepo.getCourseById(enrollment.courseId);
        if (!course) {
          console.log('[CertificationUseCase.getStudentCertifications] Course not found, skipping');
          continue;
        }

        // Get progress (simplified)
        const progress = Math.floor(Math.random() * 100); // Would calculate actual progress
        console.log('[CertificationUseCase.getStudentCertifications] Course progress:', progress, '%');

        // Check for certificate
        const certificates = await this.certificateRepo.getCertificates({
          userId: userId,
          courseId: enrollment.courseId
        });

        enrolledCourses.push({
          course,
          progress,
          status: enrollment.enrollmentStatus,
          certificate: certificates[0]
        });
      }

      console.log('[CertificationUseCase.getStudentCertifications] Getting all certificates');
      // Get all certificates for user
      const completedCertifications = await this.certificateRepo.getCertificates({
        userId: userId
      });
      console.log('[CertificationUseCase.getStudentCertifications] Found', completedCertifications.length, 'certificates');

      const result = {
        enrolledCourses,
        completedCertifications
      };
      console.log('[CertificationUseCase.getStudentCertifications] SUCCESS - Enrolled courses:', enrolledCourses.length, 'Certificates:', completedCertifications.length);
      return result;
    } catch (error) { 
      console.error('[CertificationUseCase.getStudentCertifications] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get instructor's certifications with stats
   */
  async getInstructorCertifications(
    instructorId: string
  ): Promise<{
    courses: GetCourseData[];
    stats: {
      totalStudents: number;
      totalRevenue: number;
      averageRating: number;
    };
  }> {
    console.log('[CertificationUseCase.getInstructorCertifications] START - InstructorId:', instructorId);
    try {
      console.log('[CertificationUseCase.getInstructorCertifications] Getting instructor courses');
      // Get instructor's courses
      const courses = await this.courseRepo.getCoursesByInstructor(instructorId);
      console.log('[CertificationUseCase.getInstructorCertifications] Found', courses.length, 'courses');

      console.log('[CertificationUseCase.getInstructorCertifications] Calculating stats');
      // Calculate stats (simplified)
      let totalStudents = 0;
      let totalRevenue = 0;
      let totalRating = 0;

      for (const course of courses) {
        // Get enrollments for this course
        const enrollments = await this.courseRepo.getCourseEnrollments(course.id);
        totalStudents += enrollments.length;
        totalRevenue += enrollments.length * course.price;
        console.log('[CertificationUseCase.getInstructorCertifications] Course', course.title, 'has', enrollments.length, 'students');

        // Average rating would be calculated from reviews
        totalRating += Math.floor(Math.random() * 5) + 1; // Random rating for demo
      }

      const result = {
        courses,
        stats: {
          totalStudents,
          totalRevenue,
          averageRating: courses.length > 0 ? totalRating / courses.length : 0
        }
      };
      console.log('[CertificationUseCase.getInstructorCertifications] SUCCESS - Courses:', courses.length, 'Students:', totalStudents, 'Revenue:', totalRevenue);
      return result;
    } catch (error) { 
      console.error('[CertificationUseCase.getInstructorCertifications] ERROR:', error); 
      throw error; 
    }
  }

  /**
   * Get all courses (admin level - no filtering)
   * Used by admin panel for course management
   */
  async getAllCourses(limit?: number): Promise<{
    courses: GetCourseData[];
    total: number;
    hasMore: boolean;
  }> {
    try {
      console.log('[CertificationUseCase.getAllCourses] START - Limit:', limit);
      const courses = await this.courseRepo.getCourses({}, limit);
      console.log('[CertificationUseCase.getAllCourses] SUCCESS - Retrieved', courses.length, 'courses');

      return {
        courses,
        total: courses.length,
        hasMore: false // Admin gets all courses
      };
    } catch (error) {
      console.error('[CertificationUseCase.getAllCourses] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get course by ID with full details
   */
  async getCourseById(courseId: string): Promise<GetCourseData | null> {
    try {
      console.log('[CertificationUseCase.getCourseById] START - CourseId:', courseId);
      const course = await this.courseRepo.getCourseById(courseId);
      console.log('[CertificationUseCase.getCourseById] SUCCESS - Course found:', course ? 'Yes' : 'No');
      return course;
    } catch (error) {
      console.error('[CertificationUseCase.getCourseById] ERROR:', error);
      throw error;
    }
  }
}
